import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Tabs, Form, message } from 'antd';
import {
  UserOutlined, CalendarOutlined, PartitionOutlined, DesktopOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import {
  fetchStaffAttendance, fetchMonthlyAttendanceMatrix, saveStaffAttendance, recalculateStaffAttendance,
  fetchBiometricMappings, saveBiometricMapping, deleteBiometricMapping, deleteAllBiometricMappings,
  syncAllUsersToBiometricDevice, pushSingleUserToBiometricDevice,
  fetchBiometricDevices, saveBiometricDevice, deleteBiometricDevice,
  pingBiometricDevice, requestBiometricAttendanceImport, clearBiometricDeviceUsers,
  fetchRawPunchLogs, clearAllPunchLogs, fetchServerNetworkInfo, simulateBiometricPunch,
  bulkUploadAttendance, bulkUploadMappings, autoImportBiometricMappings,
  fetchShiftSettings, saveShiftSettings, deleteShiftSettings
} from '../../../Service/Biometric_API/biometricApi';
import { fetchClusters, fetchClusterStandards } from '../../../Service/Clusters_API/clustersApi';
import { fetchStaff } from '../../../Service/Staff_API/staffApi';
import { fetchAcademicYears, fetchStandards } from '../../../Service/Syllabus_API/syllabusApi';
import { fetchCandidates } from '../../../Service/Candidates_API/candidatesApi';
import { getSocket } from '../../../Hooks/useRealTime';
import { exportColoredMatrixToExcel } from './attendanceUtils';

// Sub-Tabs
import DailyAttendanceTab from './Tabs/DailyAttendanceTab';
import MonthlyMatrixTab from './Tabs/MonthlyMatrixTab';
import BiometricMappingTab from './Tabs/BiometricMappingTab';
import DeviceSetupTab from './Tabs/DeviceSetupTab';

// Modals
import AttendanceEntryModal from './Modals/AttendanceEntryModal';
import BulkUploadAttendanceModal from './Modals/BulkUploadAttendanceModal';
import BiometricMappingModal from './Modals/BiometricMappingModal';
import AutoImportMappingsModal from './Modals/AutoImportMappingsModal';
import DeviceModal from './Modals/DeviceModal';
import PushUsersModal from './Modals/PushUsersModal';
import ShiftSettingsModal from './Modals/ShiftSettingsModal';

export default function AttendanceManagementView({ userRole }) {
  const isStudio = userRole === 'STUDIO';
  const [activeTab, setActiveTab] = useState('staff-attendance');

  // Common Reference Data
  const [staffList, setStaffList] = useState([]);
  const [candidatesList, setCandidatesList] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [standardsList, setStandardsList] = useState([]);
  const [clustersList, setClustersList] = useState([]);

  // Common Filter States
  const [clusterFilter, setClusterFilter] = useState('all');
  const [combinationFilter, setCombinationFilter] = useState('all');

  // Tab 1: Daily Attendance States
  const [attendanceData, setAttendanceData] = useState([]);
  const [attLoading, setAttLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [personTypeFilter, setPersonTypeFilter] = useState(isStudio ? 'CANDIDATE' : 'all');
  const [ayFilter, setAyFilter] = useState('all');
  const [standardFilter, setStandardFilter] = useState('all');
  const [attSearch, setAttSearch] = useState('');
  const [attModalOpen, setAttModalOpen] = useState(false);
  const [editingAtt, setEditingAtt] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [attForm] = Form.useForm();

  // Shift Settings Modal States
  const [shiftSettings, setShiftSettings] = useState([]);
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [savingShift, setSavingShift] = useState(false);

  // Tab 2: Monthly Matrix View States
  const [monthlyMatrixData, setMonthlyMatrixData] = useState({ matrix: [], dateList: [] });
  const [matrixMonth, setMatrixMonth] = useState(dayjs());
  const [matrixPersonType, setMatrixPersonType] = useState(isStudio ? 'CANDIDATE' : 'STAFF');
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixRecalculating, setMatrixRecalculating] = useState(false);

  // Tab 3: Biometric User Mappings States
  const [mappings, setMappings] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [editingMap, setEditingMap] = useState(null);
  const [mapForm] = Form.useForm();
  const [mapPersonType, setMapPersonType] = useState(isStudio ? 'CANDIDATE' : 'STAFF');
  const [mappingViewMode, setMappingViewMode] = useState('cluster-wise'); // 'cluster-wise' or 'table'
  const [mapSearch, setMapSearch] = useState('');

  // Tab 4: Devices & Raw Logs States
  const [devices, setDevices] = useState([]);
  const [rawLogs, setRawLogs] = useState([]);
  const [serverNetInfo, setServerNetInfo] = useState(null);
  const [devLoading, setDevLoading] = useState(false);
  const [devModalOpen, setDevModalOpen] = useState(false);
  const [editingDev, setEditingDev] = useState(null);
  const [pingingDevId, setPingingDevId] = useState(null);
  const [devForm] = Form.useForm();

  // Push Data to Device Modal States
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [pushTargetDevId, setPushTargetDevId] = useState('ALL');
  const [pushPersonType, setPushPersonType] = useState('ALL');
  const [pushingData, setPushingData] = useState(false);

  // Bulk Upload Modal States
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkType, setBulkType] = useState('ATTENDANCE'); // 'ATTENDANCE' or 'MAPPING'
  const [parsedRecords, setParsedRecords] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);

  // Cascading Standards State for Main Page
  const [mainClusterStandards, setMainClusterStandards] = useState([]);

  // Auto-Import Modal Cascading States
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importSelectedAy, setImportSelectedAy] = useState('all');
  const [importSelectedCluster, setImportSelectedCluster] = useState('all');
  const [importClusterStandards, setImportClusterStandards] = useState([]);
  const [importForm] = Form.useForm();

  // --- Load Initial Reference Data ---
  useEffect(() => {
    fetchAcademicYears()
      .then(res => {
        const ays = res || [];
        setAcademicYears(ays);
        if (ays.length > 0) {
          const activeAy = ays.find(a => a.status === 'Active' || a.is_current || a.is_active || String(a.is_current) === '1') || ays[0];
          if (activeAy && activeAy.academic_year_id) {
            const defaultAyId = String(activeAy.academic_year_id);
            setAyFilter(defaultAyId);
            setImportSelectedAy(defaultAyId);
          }
        }
      })
      .catch(() => setAcademicYears([]));

    fetchStandards()
      .then(res => setStandardsList(res || []))
      .catch(() => setStandardsList([]));

    fetchStaff({ status: 'Active' })
      .then(res => {
        const active = (res || []).filter(s => (s.status || '').toLowerCase() === 'active');
        setStaffList(active);
      })
      .catch(() => setStaffList([]));

    fetchCandidates()
      .then(res => setCandidatesList(res || []))
      .catch(() => setCandidatesList([]));

    fetchClusters()
      .then(res => setClustersList(res || []))
      .catch(() => setClustersList([]));

    fetchShiftSettings()
      .then(res => setShiftSettings(res || []))
      .catch(() => setShiftSettings([]));
  }, []);

  // Main Page Cascading Filter Handlers & Computed Lists
  useEffect(() => {
    if (clusterFilter && clusterFilter !== 'all') {
      fetchClusterStandards(clusterFilter, ayFilter !== 'all' ? ayFilter : undefined)
        .then(res => setMainClusterStandards(res || []))
        .catch(() => setMainClusterStandards([]));
    } else {
      setMainClusterStandards([]);
    }
  }, [clusterFilter, ayFilter]);

  const availableClusters = useMemo(() => {
    if (ayFilter && ayFilter !== 'all') {
      return clustersList.filter(c => String(c.academic_year_id) === String(ayFilter));
    }
    return clustersList;
  }, [clustersList, ayFilter]);

  const availableStandards = useMemo(() => {
    if (clusterFilter !== 'all' && mainClusterStandards.length > 0) {
      return mainClusterStandards;
    }
    if (ayFilter !== 'all') {
      return standardsList.filter(s => String(s.academic_year_id) === String(ayFilter));
    }
    return standardsList;
  }, [standardsList, ayFilter, clusterFilter, mainClusterStandards]);

  const availableCombinations = useMemo(() => {
    let filteredCands = candidatesList || [];
    if (ayFilter && ayFilter !== 'all') {
      filteredCands = filteredCands.filter(c => String(c.academic_year_id) === String(ayFilter));
    }
    if (clusterFilter && clusterFilter !== 'all') {
      filteredCands = filteredCands.filter(c => String(c.cluster_id) === String(clusterFilter) || String(c.cluster_institution_id) === String(clusterFilter));
    }
    if (standardFilter && standardFilter !== 'all') {
      filteredCands = filteredCands.filter(c => String(c.standard_id) === String(standardFilter));
    }
    const fromCandidates = filteredCands.map(c => c.combination).filter(Boolean);
    const fromMatrix = (monthlyMatrixData?.matrix || []).map(m => m.combination).filter(Boolean);
    return Array.from(new Set([...fromCandidates, ...fromMatrix])).sort();
  }, [candidatesList, monthlyMatrixData, ayFilter, clusterFilter, standardFilter]);

  // Cascading resets and defaults when parent filters change
  useEffect(() => {
    if (isStudio) {
      if (availableClusters.length > 0) {
        if (clusterFilter === 'all' || !availableClusters.some(c => String(c.cluster_id) === String(clusterFilter))) {
          setClusterFilter(String(availableClusters[0].cluster_id));
        }
      }
    } else {
      if (clusterFilter !== 'all' && availableClusters.length > 0 && !availableClusters.some(c => String(c.cluster_id) === String(clusterFilter))) {
        setClusterFilter('all');
      }
    }
  }, [availableClusters, clusterFilter, isStudio]);

  useEffect(() => {
    if (standardFilter !== 'all' && availableStandards.length > 0 && !availableStandards.some(s => String(s.standard_id) === String(standardFilter))) {
      setStandardFilter('all');
    }
  }, [availableStandards, standardFilter]);

  useEffect(() => {
    if (combinationFilter !== 'all' && availableCombinations.length > 0 && !availableCombinations.includes(combinationFilter)) {
      setCombinationFilter('all');
    }
  }, [availableCombinations, combinationFilter]);

  const importAvailableClusters = useMemo(() => {
    if (importSelectedAy && importSelectedAy !== 'all') {
      const filtered = clustersList.filter(c => String(c.academic_year_id) === String(importSelectedAy));
      if (filtered.length > 0) return filtered;
    }
    return clustersList;
  }, [clustersList, importSelectedAy]);

  const importAvailableStandards = useMemo(() => {
    if (importSelectedCluster && importSelectedCluster !== 'all' && importClusterStandards.length > 0) {
      return importClusterStandards;
    }
    if (importSelectedAy && importSelectedAy !== 'all') {
      return standardsList.filter(s => String(s.academic_year_id) === String(importSelectedAy));
    }
    return standardsList;
  }, [importSelectedCluster, importClusterStandards, importSelectedAy, standardsList]);

  // Auto-Import Modal Cascading Handlers & Computed Lists
  useEffect(() => {
    if (importModalOpen && importSelectedCluster && importSelectedCluster !== 'all') {
      fetchClusterStandards(importSelectedCluster, importSelectedAy !== 'all' ? importSelectedAy : undefined)
        .then(res => setImportClusterStandards(res || []))
        .catch(() => setImportClusterStandards([]));
    } else {
      setImportClusterStandards([]);
    }
  }, [importModalOpen, importSelectedCluster, importSelectedAy]);

  useEffect(() => {
    if (isStudio) {
      if (importAvailableClusters.length > 0) {
        if (importSelectedCluster === 'all' || !importAvailableClusters.some(c => String(c.cluster_id) === String(importSelectedCluster))) {
          setImportSelectedCluster(String(importAvailableClusters[0].cluster_id));
        }
      }
    } else {
      if (importSelectedCluster !== 'all' && importAvailableClusters.length > 0 && !importAvailableClusters.some(c => String(c.cluster_id) === String(importSelectedCluster))) {
        setImportSelectedCluster('all');
      }
    }
  }, [importAvailableClusters, importSelectedCluster, isStudio]);

  // --- API Loaders ---
  const loadAttendance = useCallback(async () => {
    setAttLoading(true);
    try {
      const isStaff = personTypeFilter === 'STAFF';
      const params = {
        date: selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined,
        person_type: isStudio ? 'CANDIDATE' : (personTypeFilter !== 'all' ? personTypeFilter : undefined),
        academic_year_id: ayFilter !== 'all' ? ayFilter : undefined,
        standard_id: (!isStaff && standardFilter !== 'all') ? standardFilter : undefined,
        cluster_id: (!isStaff && clusterFilter !== 'all') ? clusterFilter : undefined,
        combination: (!isStaff && combinationFilter !== 'all') ? combinationFilter : undefined
      };
      const res = await fetchStaffAttendance(params);
      setAttendanceData(res || []);
    } catch (error) {
      message.error(error.message || 'Failed to load attendance records');
    } finally {
      setAttLoading(false);
    }
  }, [selectedDate, personTypeFilter, ayFilter, standardFilter, clusterFilter, combinationFilter, isStudio]);

  const handleRecalculateAttendance = useCallback(async () => {
    setRecalculating(true);
    try {
      const dateStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined;
      const res = await recalculateStaffAttendance({ date: dateStr });
      message.success(res.message || 'Attendance recalculated successfully');
      await loadAttendance();
    } catch (error) {
      message.error(error.message || 'Failed to recalculate attendance');
    } finally {
      setRecalculating(false);
    }
  }, [selectedDate, loadAttendance]);

  const loadMonthlyMatrix = useCallback(async () => {
    setMatrixLoading(true);
    try {
      const formattedMonth = matrixMonth ? matrixMonth.format('YYYY-MM') : dayjs().format('YYYY-MM');
      const isStaff = matrixPersonType === 'STAFF';
      const params = {
        month: formattedMonth,
        person_type: isStudio ? 'CANDIDATE' : matrixPersonType,
        academic_year_id: ayFilter !== 'all' ? ayFilter : undefined,
        standard_id: (!isStaff && standardFilter !== 'all') ? standardFilter : undefined,
        cluster_id: clusterFilter !== 'all' ? clusterFilter : undefined,
        combination: (!isStaff && combinationFilter !== 'all') ? combinationFilter : undefined
      };
      const res = await fetchMonthlyAttendanceMatrix(params);
      setMonthlyMatrixData(res || { matrix: [], dateList: [] });
    } catch (error) {
      message.error(error.message || 'Failed to load monthly matrix');
    } finally {
      setMatrixLoading(false);
    }
  }, [matrixMonth, matrixPersonType, ayFilter, standardFilter, clusterFilter, combinationFilter, isStudio]);

  const handleRecalculateMonthlyMatrix = useCallback(async () => {
    setMatrixRecalculating(true);
    try {
      const formattedMonth = matrixMonth ? matrixMonth.format('YYYY-MM') : dayjs().format('YYYY-MM');
      const startOfMonth = matrixMonth ? matrixMonth.startOf('month').format('YYYY-MM-DD') : dayjs().startOf('month').format('YYYY-MM-DD');
      const endOfMonth = matrixMonth ? matrixMonth.endOf('month').format('YYYY-MM-DD') : dayjs().endOf('month').format('YYYY-MM-DD');
      
      const res = await recalculateStaffAttendance({
        month: formattedMonth,
        startDate: startOfMonth,
        endDate: endOfMonth,
        person_type: isStudio ? 'CANDIDATE' : matrixPersonType
      });
      message.success(res.message || 'Monthly attendance recalculated successfully');
      await loadMonthlyMatrix();
    } catch (error) {
      message.error(error.message || 'Failed to recalculate monthly attendance');
    } finally {
      setMatrixRecalculating(false);
    }
  }, [matrixMonth, matrixPersonType, isStudio, loadMonthlyMatrix]);

  const loadMappings = useCallback(async () => {
    setMapLoading(true);
    try {
      const params = {
        academic_year_id: ayFilter !== 'all' ? ayFilter : undefined,
        standard_id: standardFilter !== 'all' ? standardFilter : undefined,
        cluster_id: clusterFilter !== 'all' ? clusterFilter : undefined,
        combination: combinationFilter !== 'all' ? combinationFilter : undefined
      };
      const res = await fetchBiometricMappings(params);
      setMappings(res || []);
    } catch (error) {
      message.error(error.message || 'Failed to load biometric mappings');
    } finally {
      setMapLoading(false);
    }
  }, [ayFilter, standardFilter, clusterFilter, combinationFilter]);

  const loadDevicesAndLogs = useCallback(async (showToast = false) => {
    setDevLoading(true);
    try {
      const [devRes, logsRes, netRes] = await Promise.all([
        fetchBiometricDevices(),
        fetchRawPunchLogs(),
        fetchServerNetworkInfo().catch(() => null)
      ]);
      setDevices(devRes || []);
      setRawLogs(logsRes || []);
      if (netRes) setServerNetInfo(netRes);
      if (showToast) message.success('Biometric device status and live stream refreshed');
    } catch (error) {
      if (showToast) message.error(error.message || 'Failed to refresh devices');
    } finally {
      setDevLoading(false);
    }
  }, []);

  // Tab change trigger
  useEffect(() => {
    if (activeTab === 'staff-attendance' || activeTab === 'candidate-attendance') loadAttendance();
    if (activeTab === 'monthly-matrix') loadMonthlyMatrix();
    if (activeTab === 'biometric-mapping') loadMappings();
    if (activeTab === 'device-setup') {
      loadDevicesAndLogs();
      const interval = setInterval(() => loadDevicesAndLogs(false), 8000);
      return () => clearInterval(interval);
    }
  }, [activeTab, loadAttendance, loadMonthlyMatrix, loadMappings, loadDevicesAndLogs]);

  // Real-time WebSocket event handling
  useEffect(() => {
    const socket = getSocket();
    const handlePunch = (punch) => {
      if (punch) {
        message.info(`Biometric Punch: User ${punch.biometric_user_id} (${punch.punch_state || 'PUNCH'})`);
        setRawLogs(prev => [punch, ...prev.slice(0, 49)]);
        if (activeTab === 'staff-attendance' || activeTab === 'candidate-attendance') loadAttendance();
        if (activeTab === 'monthly-matrix') loadMonthlyMatrix();
      }
    };
    const handleDevStatus = (devStatus) => {
      if (devStatus) {
        setDevices(prev => prev.map(d => (d.device_id === devStatus.device_id ? { ...d, ...devStatus } : d)));
      }
    };

    socket.on('biometric_punch', handlePunch);
    socket.on('biometric_device_status', handleDevStatus);

    return () => {
      socket.off('biometric_punch', handlePunch);
      socket.off('biometric_device_status', handleDevStatus);
    };
  }, [activeTab, loadAttendance, loadMonthlyMatrix]);

  // --- Action Handlers: Attendance ---
  const handleOpenAttModal = (record = null, presetPersonType = null, presetDate = null) => {
    setEditingAtt(record);
    if (!record) {
      attForm.resetFields();
      attForm.setFieldsValue({
        person_type: presetPersonType || (isStudio ? 'CANDIDATE' : 'STAFF'),
        attendance_date: presetDate || selectedDate || dayjs(),
        status: 'PRESENT',
        academic_year_id: ayFilter !== 'all' ? parseInt(ayFilter) : undefined
      });
    }
    setAttModalOpen(true);
  };

  const handleSaveAtt = async () => {
    try {
      const values = await attForm.validateFields();
      const dateStr = values.attendance_date ? values.attendance_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

      const formatDateTime = (timeObj) => {
        if (!timeObj) return null;
        if (dayjs.isDayjs(timeObj)) {
          return `${dateStr} ${timeObj.format('HH:mm:ss')}`;
        }
        const s = String(timeObj).trim();
        if (s.includes('T') || s.includes(' ')) return s;
        return `${dateStr} ${s}`;
      };

      const payload = {
        attendance_id: editingAtt?.attendance_id || undefined,
        person_type: values.person_type,
        staff_id: values.person_type === 'STAFF' ? values.staff_id : null,
        candidate_id: values.person_type === 'CANDIDATE' ? values.candidate_id : null,
        biometric_user_id: values.biometric_user_id || null,
        academic_year_id: values.academic_year_id || null,
        attendance_date: dateStr,
        check_in_time: formatDateTime(values.check_in_time),
        check_out_time: formatDateTime(values.check_out_time),
        status: values.status,
        remarks: values.remarks || null
      };
      await saveStaffAttendance(payload);
      message.success('Attendance saved successfully');
      setAttModalOpen(false);
      setEditingAtt(null);
      loadAttendance();
      if (activeTab === 'monthly-matrix') loadMonthlyMatrix();
    } catch (error) {
      console.error('Failed to save attendance:', error);
      message.error(error.message || 'Failed to save attendance record');
    }
  };

  const handleSaveShiftSettings = async (payload) => {
    try {
      setSavingShift(true);
      const res = await saveShiftSettings(payload);
      const updated = await fetchShiftSettings();
      setShiftSettings(updated || []);
      return res;
    } catch (error) {
      throw error;
    } finally {
      setSavingShift(false);
    }
  };

  const handleDeleteShiftSetting = async (id) => {
    try {
      await deleteShiftSettings(id);
      message.success('Shift setting rule deleted');
      const updated = await fetchShiftSettings();
      setShiftSettings(updated || []);
    } catch (error) {
      message.error(error.message || 'Failed to delete shift setting');
    }
  };

  // --- Action Handlers: Mappings ---
  const handleOpenMapModal = (item = null) => {
    setEditingMap(item);
    if (item) {
      setMapPersonType(item.person_type || 'STAFF');
      mapForm.setFieldsValue({
        ...item,
        device_role: item.device_role || 'USER'
      });
    } else {
      mapForm.resetFields();
      setMapPersonType(isStudio ? 'CANDIDATE' : 'STAFF');
      mapForm.setFieldsValue({
        person_type: isStudio ? 'CANDIDATE' : 'STAFF',
        device_role: 'USER',
        status: 'Active',
        academic_year_id: ayFilter !== 'all' ? parseInt(ayFilter) : undefined
      });
    }
    setMapModalOpen(true);
  };

  const handleSaveMap = async () => {
    try {
      const values = await mapForm.validateFields();
      const payload = {
        mapping_id: editingMap?.mapping_id,
        biometric_user_id: values.biometric_user_id,
        badge_number: values.badge_number || null,
        device_role: values.device_role || 'USER',
        person_type: values.person_type,
        staff_id: values.person_type === 'STAFF' ? values.staff_id : null,
        candidate_id: values.person_type === 'CANDIDATE' ? values.candidate_id : null,
        academic_year_id: values.academic_year_id || null,
        status: values.status
      };
      await saveBiometricMapping(payload);
      message.success('Biometric user mapping saved');
      setMapModalOpen(false);
      loadMappings();
    } catch (error) {
      message.error(error.message || 'Failed to save user mapping');
    }
  };

  const handlePushSingleUser = async (id) => {
    try {
      const res = await pushSingleUserToBiometricDevice(id);
      message.success(res.message || 'User pushed to biometric machine');
    } catch (error) {
      message.error(error.message || 'Failed to push user to biometric machine');
    }
  };

  const handleDeleteMap = async (id) => {
    try {
      await deleteBiometricMapping(id);
      message.success('Biometric mapping removed');
      loadMappings();
    } catch (error) {
      message.error(error.message || 'Failed to delete mapping');
    }
  };

  const handleDeleteAllMappings = async () => {
    try {
      const res = await deleteAllBiometricMappings();
      message.success(res.message || 'All mapped biometric users deleted');
      loadMappings();
    } catch (error) {
      message.error(error.message || 'Failed to delete mappings');
    }
  };

  const handleOpenImportModal = () => {
    importForm.resetFields();
    const initialAy = ayFilter || 'all';
    const initialCluster = clusterFilter || 'all';
    const initialStd = standardFilter || 'all';
    setImportSelectedAy(initialAy);
    setImportSelectedCluster(initialCluster);
    importForm.setFieldsValue({
      personType: isStudio ? 'CANDIDATE' : 'ALL',
      idStrategy: 'ID_OR_CODE',
      academicYearId: initialAy,
      clusterId: initialCluster,
      standardId: initialStd,
      overwriteExisting: true
    });
    setImportModalOpen(true);
  };

  const handleAutoImportMappings = async () => {
    try {
      setImportLoading(true);
      const values = await importForm.validateFields();
      const payload = {
        personType: values.personType,
        idStrategy: values.idStrategy,
        academicYearId: values.academicYearId !== 'all' ? values.academicYearId : undefined,
        clusterId: values.clusterId !== 'all' ? values.clusterId : undefined,
        standardId: values.standardId !== 'all' ? values.standardId : undefined,
        overwriteExisting: values.overwriteExisting || false,
        startSeqId: 1001
      };
      const res = await autoImportBiometricMappings(payload);
      message.success(res.message || 'Auto-import completed successfully');
      setImportModalOpen(false);
      loadMappings();
    } catch (error) {
      message.error(error.message || 'Failed to auto-import users');
    } finally {
      setImportLoading(false);
    }
  };

  const handleOpenPushModal = (targetDev = 'ALL') => {
    setPushTargetDevId(targetDev);
    setPushPersonType(isStudio ? 'CANDIDATE' : 'ALL');
    setPushModalOpen(true);
  };

  const handleExecutePush = async () => {
    try {
      setPushingData(true);
      const payload = {
        device_id: pushTargetDevId !== 'ALL' ? pushTargetDevId : null,
        person_type: pushPersonType
      };
      const res = await syncAllUsersToBiometricDevice(payload);
      message.success(res.message || 'User profiles queued for sync to biometric machine');
      setPushModalOpen(false);
    } catch (error) {
      message.error(error.message || 'Failed to push users to machine');
    } finally {
      setPushingData(false);
    }
  };

  // --- Action Handlers: Devices ---
  const handleOpenDevModal = (item = null) => {
    setEditingDev(item);
    if (item) {
      devForm.setFieldsValue({
        ...item,
        machine_number: item.machine_number || 1,
        comm_key: item.comm_key || '0'
      });
    } else {
      devForm.resetFields();
      devForm.setFieldsValue({
        device_type: 'eSSL K30',
        port: 4370,
        machine_number: 1,
        comm_key: '0',
        status: 'ONLINE'
      });
    }
    setDevModalOpen(true);
  };

  const handleSaveDev = async () => {
    try {
      const values = await devForm.validateFields();
      const payload = {
        device_id: editingDev?.device_id,
        ...values,
        machine_number: values.machine_number || 1,
        comm_key: values.comm_key || '0',
        port: values.port ? parseInt(values.port) : 4370
      };
      await saveBiometricDevice(payload);
      message.success('Device settings saved');
      setDevModalOpen(false);
      loadDevicesAndLogs(false);
    } catch (error) {
      message.error(error.message || 'Failed to save device');
    }
  };

  const handleDeleteDev = async (id) => {
    try {
      await deleteBiometricDevice(id);
      message.success('Device removed');
      loadDevicesAndLogs(false);
    } catch (error) {
      message.error(error.message || 'Failed to delete device');
    }
  };

  const handlePingDevice = async (record) => {
    setPingingDevId(record.device_id);
    try {
      const res = await pingBiometricDevice(record.device_id);
      if (res.isReachable) message.success(res.message || 'Device is ONLINE & REACHABLE');
      else message.warning(res.message || 'Device is OFFLINE / UNREACHABLE');
      loadDevicesAndLogs(false);
    } catch (error) {
      message.error(error.message || 'Failed to test connection');
    } finally {
      setPingingDevId(null);
    }
  };

  const handleRequestAttendanceImport = async ({ device_id, serial_number }) => {
    try {
      const res = await requestBiometricAttendanceImport({ device_id, serial_number });
      message.success(res.message || 'Attendance sync command dispatched to machine');
    } catch (error) {
      message.error(error.message || 'Failed to request attendance import');
    }
  };

  const handleClearDeviceUsers = async ({ device_id, serial_number }) => {
    try {
      const res = await clearBiometricDeviceUsers({ device_id, serial_number });
      message.success(res.message || 'Cleared all users from physical biometric machine');
    } catch (error) {
      message.error(error.message || 'Failed to clear users from device');
    }
  };

  const handleClearAllLogs = async () => {
    try {
      await clearAllPunchLogs();
      setRawLogs([]);
      message.success('All raw punch logs cleared');
    } catch (error) {
      message.error(error.message || 'Failed to clear punch logs');
    }
  };

  const handleSimulatePunch = async () => {
    try {
      const samplePin = mappings.length > 0 ? mappings[0].biometric_user_id : '101';
      await simulateBiometricPunch({ biometric_user_id: samplePin, punch_state: 'IN' });
      message.success(`Test punch generated for PIN: ${samplePin}`);
      loadDevicesAndLogs(false);
    } catch (error) {
      message.error(error.message || 'Failed to simulate punch');
    }
  };

  // --- Action Handlers: Bulk Upload & Templates ---
  const handleOpenBulkModal = (type = 'ATTENDANCE') => {
    setBulkType(type);
    setParsedRecords([]);
    setBulkModalOpen(true);
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        if (file.name.endsWith('.json')) {
          const parsed = JSON.parse(text);
          setParsedRecords(Array.isArray(parsed) ? parsed : [parsed]);
        } else {
          const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
          if (lines.length < 2) {
            message.error('CSV file has no data rows');
            return;
          }
          const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
          const rows = [];
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
            const obj = {};
            headers.forEach((h, idx) => {
              obj[h] = values[idx] || '';
            });
            rows.push(obj);
          }
          setParsedRecords(rows);
        }
        message.success(`Parsed ${parsedRecords.length || 'rows'} successfully from file`);
      } catch (err) {
        message.error('Failed to parse file: ' + err.message);
      }
    };
    reader.readAsText(file);
    return false;
  };

  const handleSubmitBulk = async () => {
    if (!parsedRecords.length) {
      message.warning('No records to upload');
      return;
    }
    setBulkUploading(true);
    try {
      if (bulkType === 'ATTENDANCE') {
        const res = await bulkUploadAttendance({ records: parsedRecords });
        message.success(`Bulk upload complete: ${res.successCount || parsedRecords.length} records saved`);
        loadAttendance();
        if (activeTab === 'monthly-matrix') loadMonthlyMatrix();
      } else {
        const res = await bulkUploadMappings({ mappings: parsedRecords });
        message.success(`Bulk upload complete: ${res.successCount || parsedRecords.length} mappings saved`);
        loadMappings();
      }
      setBulkModalOpen(false);
    } catch (error) {
      message.error(error.message || 'Failed to upload records');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleDownloadAttendanceTemplate = () => {
    const headers = ['person_type', 'staff_id', 'candidate_id', 'biometric_user_id', 'academic_year_id', 'attendance_date', 'check_in_time', 'check_out_time', 'status', 'remarks'];
    const sampleRows = [
      ['STAFF', '1', '', '101', '1', dayjs().format('YYYY-MM-DD'), '09:00:00', '18:00:00', 'PRESENT', 'On time'],
      ['CANDIDATE', '', '40224', '40224', '1', dayjs().format('YYYY-MM-DD'), '09:15:00', '17:45:00', 'LATE', 'Late entry']
    ];
    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `attendance_upload_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadMappingTemplate = () => {
    const headers = ['biometric_user_id', 'badge_number', 'device_role', 'person_type', 'staff_id', 'candidate_id', 'academic_year_id', 'status'];
    const sampleRows = [
      ['101', '0009876543', 'USER', 'STAFF', '1', '', '1', 'Active'],
      ['40224', '', 'USER', 'CANDIDATE', '', '40224', '1', 'Active']
    ];
    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `biometric_mapping_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportMonthlyMatrixCSV = () => {
    if (!monthlyMatrixData.matrix || !monthlyMatrixData.matrix.length) {
      message.warning('No matrix data available to export');
      return;
    }
    exportColoredMatrixToExcel(
      monthlyMatrixData.matrix,
      monthlyMatrixData.dateList || [],
      `attendance_monthly_matrix_${matrixMonth.format('YYYY_MM')}`
    );
  };

  return (
    <div className="attendance-management-wrapper" style={{ padding: 20, minHeight: '100vh', background: 'var(--bg-color)' }}>
      {/* Main Tabs Navigation */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
        className="attendance-main-tabs"
        style={{ color: 'var(--text-color)' }}
        items={[
          {
            key: 'staff-attendance',
            label: (
              <span>
                <UserOutlined /> Daily Attendance List
              </span>
            ),
            children: (
              <DailyAttendanceTab
                attendanceData={attendanceData}
                attLoading={attLoading}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                personTypeFilter={personTypeFilter}
                setPersonTypeFilter={setPersonTypeFilter}
                ayFilter={ayFilter}
                setAyFilter={setAyFilter}
                clusterFilter={clusterFilter}
                setClusterFilter={setClusterFilter}
                standardFilter={standardFilter}
                setStandardFilter={setStandardFilter}
                combinationFilter={combinationFilter}
                setCombinationFilter={setCombinationFilter}
                attSearch={attSearch}
                setAttSearch={setAttSearch}
                academicYears={academicYears}
                availableClusters={availableClusters}
                availableStandards={availableStandards}
                availableCombinations={availableCombinations}
                isStudio={isStudio}
                onReload={loadAttendance}
                onRecalculate={handleRecalculateAttendance}
                recalculating={recalculating}
                onOpenAttModal={handleOpenAttModal}
                onOpenShiftSettingsModal={() => setShiftModalOpen(true)}
              />
            )
          },
          {
            key: 'monthly-matrix',
            label: (
              <span>
                <CalendarOutlined /> Monthly Matrix View (P/A/HD/OT)
              </span>
            ),
            children: (
              <MonthlyMatrixTab
                monthlyMatrixData={monthlyMatrixData}
                matrixMonth={matrixMonth}
                setMatrixMonth={setMatrixMonth}
                matrixPersonType={matrixPersonType}
                setMatrixPersonType={setMatrixPersonType}
                ayFilter={ayFilter}
                setAyFilter={setAyFilter}
                clusterFilter={clusterFilter}
                setClusterFilter={setClusterFilter}
                standardFilter={standardFilter}
                setStandardFilter={setStandardFilter}
                combinationFilter={combinationFilter}
                setCombinationFilter={setCombinationFilter}
                academicYears={academicYears}
                availableClusters={availableClusters}
                availableStandards={availableStandards}
                availableCombinations={availableCombinations}
                matrixLoading={matrixLoading}
                recalculating={matrixRecalculating}
                isStudio={isStudio}
                onReload={loadMonthlyMatrix}
                onRecalculate={handleRecalculateMonthlyMatrix}
                onExportCSV={handleExportMonthlyMatrixCSV}
                onOpenAttModal={handleOpenAttModal}
              />
            )
          },
          {
            key: 'biometric-mapping',
            label: (
              <span>
                <PartitionOutlined /> Mapped Biometric Users (Cluster Wise)
              </span>
            ),
            children: (
              <BiometricMappingTab
                mappings={mappings}
                mapLoading={mapLoading}
                clusterFilter={clusterFilter}
                setClusterFilter={setClusterFilter}
                ayFilter={ayFilter}
                setAyFilter={setAyFilter}
                standardFilter={standardFilter}
                setStandardFilter={setStandardFilter}
                combinationFilter={combinationFilter}
                setCombinationFilter={setCombinationFilter}
                availableCombinations={availableCombinations}
                mapSearch={mapSearch}
                setMapSearch={setMapSearch}
                mappingViewMode={mappingViewMode}
                setMappingViewMode={setMappingViewMode}
                availableClusters={availableClusters}
                academicYears={academicYears}
                availableStandards={availableStandards}
                isStudio={isStudio}
                onReload={loadMappings}
                onDownloadTemplate={handleDownloadMappingTemplate}
                onOpenBulkModal={handleOpenBulkModal}
                onOpenImportModal={handleOpenImportModal}
                onOpenPushModal={handleOpenPushModal}
                onDeleteAllMappings={handleDeleteAllMappings}
                onOpenMapModal={handleOpenMapModal}
                onDeleteMap={handleDeleteMap}
                onPushSingleUser={handlePushSingleUser}
              />
            )
          },
          {
            key: 'device-setup',
            label: (
              <span>
                <DesktopOutlined /> eSSL K30 Device & ADMS Setup
              </span>
            ),
            children: (
              <DeviceSetupTab
                devices={devices}
                rawLogs={rawLogs}
                serverNetInfo={serverNetInfo}
                devLoading={devLoading}
                pingingDevId={pingingDevId}
                onReload={() => loadDevicesAndLogs(true)}
                onOpenDevModal={handleOpenDevModal}
                onDeleteDev={handleDeleteDev}
                onPingDevice={handlePingDevice}
                onOpenPushModal={handleOpenPushModal}
                onOpenImportModal={handleOpenImportModal}
                onDeleteAllMappings={handleDeleteAllMappings}
                onRequestAttendanceImport={handleRequestAttendanceImport}
                onClearDeviceUsers={handleClearDeviceUsers}
                onClearAllLogs={handleClearAllLogs}
                onSimulatePunch={handleSimulatePunch}
              />
            )
          }
        ]}
      />

      {/* Modal: Set Individual Time Date-Wise */}
      <AttendanceEntryModal
        open={attModalOpen}
        editingRecord={editingAtt}
        form={attForm}
        onSave={handleSaveAtt}
        onCancel={() => {
          setAttModalOpen(false);
          setEditingAtt(null);
        }}
        isStudio={isStudio}
        staffList={staffList}
        candidatesList={candidatesList}
        academicYears={academicYears}
        ayFilter={ayFilter}
        standardFilter={standardFilter}
        shiftSettings={shiftSettings}
      />

      {/* Modal: Bulk Upload */}
      <BulkUploadAttendanceModal
        open={bulkModalOpen}
        bulkType={bulkType}
        parsedRecords={parsedRecords}
        bulkUploading={bulkUploading}
        onFileUpload={handleFileUpload}
        onDownloadAttendanceTemplate={handleDownloadAttendanceTemplate}
        onDownloadMappingTemplate={handleDownloadMappingTemplate}
        onSubmitBulk={handleSubmitBulk}
        onCancel={() => setBulkModalOpen(false)}
      />

      {/* Modal: Biometric Mapping */}
      <BiometricMappingModal
        open={mapModalOpen}
        editingMap={editingMap}
        form={mapForm}
        onSave={handleSaveMap}
        onCancel={() => setMapModalOpen(false)}
        mapPersonType={mapPersonType}
        setMapPersonType={setMapPersonType}
        isStudio={isStudio}
        staffList={staffList}
        candidatesList={candidatesList}
        academicYears={academicYears}
        ayFilter={ayFilter}
        standardFilter={standardFilter}
      />

      {/* Modal: Device Registration */}
      <DeviceModal
        open={devModalOpen}
        editingDev={editingDev}
        form={devForm}
        onSave={handleSaveDev}
        onCancel={() => setDevModalOpen(false)}
      />

      {/* Modal: Auto-Import Existing Candidates & Staff */}
      <AutoImportMappingsModal
        open={importModalOpen}
        importLoading={importLoading}
        form={importForm}
        onImport={handleAutoImportMappings}
        onCancel={() => setImportModalOpen(false)}
        isStudio={isStudio}
        academicYears={academicYears}
        importAvailableClusters={importAvailableClusters}
        importAvailableStandards={importAvailableStandards}
        setImportSelectedAy={setImportSelectedAy}
        setImportSelectedCluster={setImportSelectedCluster}
      />

      {/* Modal: Select Target Device to Push Data */}
      <PushUsersModal
        open={pushModalOpen}
        pushTargetDevId={pushTargetDevId}
        setPushTargetDevId={setPushTargetDevId}
        pushPersonType={pushPersonType}
        setPushPersonType={setPushPersonType}
        pushingData={pushingData}
        devices={devices}
        onExecutePush={handleExecutePush}
        onCancel={() => setPushModalOpen(false)}
      />

      {/* Modal: Shift Timings & Timing Windows */}
      <ShiftSettingsModal
        open={shiftModalOpen}
        onCancel={() => setShiftModalOpen(false)}
        shiftSettings={shiftSettings}
        availableCombinations={availableCombinations}
        staffList={staffList}
        candidatesList={candidatesList}
        isStudio={isStudio}
        onSaveShiftSettings={handleSaveShiftSettings}
        onDeleteShiftSetting={handleDeleteShiftSetting}
        saving={savingShift}
      />
    </div>
  );
}
